import { getPathNoLangOnClient } from '@/utils/tool';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { NextRouter } from 'next/router';
import { openDefaultBrowserInWeeeApp } from '@/utils/common/open-default-browser-in-weee-app';

const isGoAppLink = (link: string) => {
  return link?.indexOf('go.sayweee.com') > -1;
};

/**
 * 处理CMS跳转链接，移除语言位并检查是否与当前域名同源
 * @param link 要处理的链接
 * @returns 返回一个对象，包含处理后的链接以及是否与当前域名同源
 */
export const handleCmsJumpLink = (link: string): { link: string; isSameOrigin: boolean } => {
  const host = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  if (link.indexOf(host) === -1) {
    try {
      const temp = ensureRelativePathQAOnly(link);
      if (temp != link) {
        return { link: temp, isSameOrigin: true };
      }
    } catch (e) {}
    return { link: link, isSameOrigin: false };
  } else {
    // TODO hardcode 语言位有风险，如果业务新增了语言可能会出现bug
    return { link: link.replace(/\/(zh-Hant|zht|zh|en|es|pt|ko|ja|vi|th)\//, '/'), isSameOrigin: true };
  }
};

export const ensureRelativePath = (fullPath: string) => {
  let final = fullPath;
  try {
    if (final.startsWith('http')) {
      // <link> will auto prepend the locale, it may make mistakes
      final =
        '/' +
        final
          .split('/')
          .splice(3)
          .join('/')
          .replace(/^(zh-Hant|zht|zh|en|es|pt|ko|ja|vi|th)\//, '');
    }
  } catch (e) {}
  return final;
};

export const ensureRelativePathQAOnly = (fullPath: string) => {
  if (!fullPath) {
    return process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  }
  if (isLatinoChannel()) return fullPath;
  if (fullPath.indexOf('/api') > -1) return fullPath;
  if (isGoAppLink(fullPath)) return fullPath;
  let final = fullPath;
  try {
    try {
      const ENV = process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR;
      let isTesting =
        ENV === 'test' || ENV === 'tb1' || ENV === 'tb2' || ENV === 'development' || ENV === 'pre' || false;
      if (!isTesting && typeof window != 'undefined' && window?.location?.hostname === 'pre.sayweee.com') {
        isTesting = true;
      }
      if (isTesting) {
        final = ensureRelativePath(final);
      }
    } catch (e) {}
  } catch (e) {}

  return final;
};
export const checkIsSameUrlAndGetAnchor = (jumpLink: string) => {
  try {
    const urlObj = new URL(jumpLink, window.location.origin); // 使用 window.location.origin 以防 jumpLink 是相对路径
    const currentUrlObj = new URL(window.location.href);

    const jumpLinkWithoutLang = getPathNoLangOnClient(urlObj.pathname);
    const currentLinkWithoutLang = getPathNoLangOnClient(currentUrlObj.pathname);

    const anchor = urlObj.searchParams.get('anchor');

    return {
      isSameUrl: jumpLinkWithoutLang === currentLinkWithoutLang,
      anchor: anchor || ''
    };
  } catch (error) {
    console.error('Invalid URL:', error);
    return { isSameUrl: false, anchor: '' };
  }
};

/**
 * 处理CMS跳转操作
 * 在浏览器环境下，同一个项目页面直接的跳转使用Next.js的路由，以确保keepalive方案的有效
 * 如果是在SayWeee App 分为站内链接和站外链接两种情况
 *   站内链接：直接使用window.location.href + pushto=native 进行跳转，现象是打开新的webview
 *   站外链接：使用openDefaultBrowserInWeeeApp进行跳转，现象是打开默认浏览器
 * @param jumpLink 要跳转的CMS链接
 * @param router Next.js 路由对象
 */
export const handleCmsJump = (jumpLink: string, router: NextRouter) => {
  if (!jumpLink || typeof jumpLink !== 'string') {
    console.warn('handleCmsJump: Invalid jumpLink', jumpLink);
    return;
  }
  const { link, isSameOrigin } = handleCmsJumpLink(jumpLink);
  if (isSayWeeeApp()) {
    if (!isSameOrigin) {
      openDefaultBrowserInWeeeApp(link);
    } else {
      const delimiter = link.includes('?') ? '&' : '?';
      const newLink = `${link}${delimiter}pushto=native`;
      window.location.href = newLink;
    }
  } else {
    router.push(link);
  }
};

export const relativeLinkIfQAEnv = (jumpLink: string) => {
  if (!isSayWeeeApp()) {
    const relPath = ensureRelativePathQAOnly(jumpLink);
    return relPath;
  } else {
    return jumpLink;
  }
};
